import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Chip,
  IconButton,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import LinkOffIcon from "@mui/icons-material/LinkOff";

import { useSelector } from "react-redux";
import { setEditUnpair } from "../../../../../features/financeReceiptSlice";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import FMEStatus from "../helpers/components/FMEStatus";

const tableHeadings = {
  fme: "FME",
  date: "Date",
  fme_income: "FME Income",
  policy_fme_income: "Policy FME Income",
  manual_fme_income: "Manual FME Income",
  covered: "% Covered",
  fme_status: "FME Status",
};

const EditReceiptsLinkedFmeTable = () => {
  const headCells = Object.entries(tableHeadings);
  const dispatch = useDispatch();
  const { selectedReceipt, linkedFmes, editUnpairPayload } = useSelector(
    (state: any) => state.receipts
  );
  const round2 = (value: any) => {
    if (value === null || value === undefined || isNaN(value)) return null;
    return Math.round(Number(value) * 100) / 100;
  };

  const rows = React.useMemo(() => {
    if (!linkedFmes?.length) return [];
    const filtered = editUnpairPayload
      ? linkedFmes.filter(
          (fme: any) =>
            !editUnpairPayload.fme_shared_uids.includes(fme.fme_shared_uid)
        )
      : linkedFmes;
    return filtered.map((fme: any) => ({
      id: fme.fme_id,
      fme: fme.fme_id,
      date: fme.month,
      fme_income: round2(fme.total_income),
      policy_fme_income: round2(fme.invoice_total_income),
      manual_fme_income: round2(fme.manual_invoice_total_income),
      covered: round2(selectedReceipt.total_covered),
      fme_status: fme.status,
      currency_symbol: fme.currency_symbol,
    }));
  }, [selectedReceipt, linkedFmes, editUnpairPayload]);
  const handleEditUnpairClick = (clickedFme: any) => {
    const payload = {
      receipt_shared_uid: selectedReceipt.receipt_shared_uid,
      fme_shared_uids: [clickedFme.fme_shared_uid],
    };

    dispatch(
      setEditUnpair({
        payload,
        fme_id: clickedFme.fme_id,
      })
    );
  };

  return (
    <>
      <Typography>Linked FME Records:</Typography>
      <TableContainer
        className="receiptsTable EditReceiptsLinkedFmeTable"
        sx={{ mb: 3, mt: 1, maxHeight: "250px" }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map(([_, value], index) => (
                <TableCell key={index} align="left">
                  <TableSortLabel>
                    {value && typeof value === "string" ? value : ""}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: any) => {
              return (
                <React.Fragment key={row.id}>
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell key={cellIndex} align="left">
                        {key === "fme" ? (
                          <div className="chip-data">
                            <>
                              <Chip
                                label={row[key] ? row[key] : "N/A"}
                                sx={{
                                  backgroundColor: "#ffffff",
                                  color: "rgba(0, 0, 0, 0.87)",
                                  borderRadius: "5px",
                                  boxShadow: "1px 1px 2px 1px #00000030",
                                  height: "26px",
                                  fontWeight: "500",
                                  cursor: "pointer",
                                  border: "1px solid #ffffff",
                                  mr: 1,
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: "#9c27b0",
                                    border: "1px solid #9c27b0",
                                  },
                                }}
                              />

                              <Tooltip title="Delete" placement="top">
                                <IconButton
                                  data-tracker="593"
                                  disabled={!!editUnpairPayload}
                                  onClick={() =>
                                    handleEditUnpairClick({
                                      fme_id: row.id,
                                      fme_shared_uid: linkedFmes.find(
                                        (f: any) => f.fme_id === row.id
                                      )?.fme_shared_uid,
                                    })
                                  }
                                  sx={{
                                    width: "40px",
                                    height: "40px",
                                    color: !!editUnpairPayload
                                      ? "rgba(0,0,0,0.38)"
                                      : "#9c27b0",
                                  }}
                                >
                                  <LinkOffIcon
                                    // color="disabled"
                                    fontSize="small"
                                  />
                                </IconButton>
                              </Tooltip>
                            </>
                          </div>
                        ) : key === "date" ? (
                          <>
                            {row[key]
                              ? dayjs(row[key]).format("DD/MM/YYYY")
                              : "N/A"}
                          </>
                        ) : key === "fme_income" ? (
                          <>
                            <Box display={"flex"} alignItems={"center"}>
                              {row[key] ? row[key] : "N/A"}{" "}
                              <span style={{ marginLeft: 6, opacity: 0.7 }}>
                                {row.currency_symbol}
                              </span>
                            </Box>
                          </>
                        ) : key === "policy_fme_income" ? (
                          <>
                            <Box display={"flex"} alignItems={"center"}>
                              {row[key] ? row[key] : "N/A"}{" "}
                              <span style={{ marginLeft: 6, opacity: 0.7 }}>
                                {row.currency_symbol}
                              </span>
                            </Box>
                          </>
                        ) : key === "manual_fme_income" ? (
                          <>
                            <Box display={"flex"} alignItems={"center"}>
                              {row[key] ? row[key] : "N/A"}{" "}
                              <span style={{ marginLeft: 6, opacity: 0.7 }}>
                                {row.currency_symbol}
                              </span>
                            </Box>
                          </>
                        ) : key === "covered" ? (
                          <> {row[key] ? row[key] : "N/A"}</>
                        ) : key === "fme_status" ? (
                          <FMEStatus status={row.fme_status?.toLowerCase()} />
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default EditReceiptsLinkedFmeTable;
